import React, { useContext, useEffect, useState } from 'react';
import AppShell from '../shell/AppShell';
import DiscoveryScreen from '../../features/discovery/DiscoveryScreen';
import dataService from '../../services/dataService';
import { UserContext } from '../../UserContext';
import { useI18n } from '../../features/i18n';

/**
 * /unified/discover — the archive DiscoveryScreen (swipe deck + filter bar
 * + live Mapbox map + dietary drawer) inside the unified AppShell so the
 * top nav / footer stay consistent across every unified route.
 *
 * The DiscoveryScreen module already renders its own brand header
 * ("RESTAURANT DISCOVERY" eyebrow + logo) against a bubble-blue backdrop,
 * so we render it dense (no extra padding) and let it own the page body.
 *
 * If the logged-in user has preferences from onboarding (dietary_style,
 * allergy, preference), we pre-populate the dietary filter so Discover
 * defaults to "restaurants that work for me". Users can clear the auto
 * filter in one tap if they want to browse the whole list.
 */

// Map stored preference labels to the canonical dietary tags Discovery
// filters on. Keys are lowercase so matching is case-insensitive.
const PREF_TO_TAG = {
  'vegan':                 'Vegan',
  'vegetarian':            'Vegetarian',
  'pescatarian':           'Pescatarian',
  'gluten-free':           'Gluten-Free',
  'gluten free':           'Gluten-Free',
  'gluten':                'Gluten-Free',      // allergy
  'dairy-free':            'Dairy-Free',
  'dairy free':            'Dairy-Free',
  'dairy':                 'Dairy-Free',       // allergy
  'lactose':               'Dairy-Free',
  'nut-free':              'Nut-Free',
  'nut free':              'Nut-Free',
  'nuts':                  'Nut-Free',
  'peanut':                'Nut-Free',
  'tree nuts':             'Nut-Free',
  'shellfish':             'Shellfish-Free',
  'shellfish-free':        'Shellfish-Free',
  'egg':                   'Egg-Free',
  'eggs':                  'Egg-Free',
  'egg-free':              'Egg-Free',
  'soy':                   'Soy-Free',
  'soy-free':              'Soy-Free',
  'kosher':                'Kosher',
  'halal':                 'Halal',
  'keto':                  'Keto',
  'paleo':                 'Paleo',
  'low-carb':              'Low-Carb',
  'low carb':              'Low-Carb',
};

export default function UnifiedDiscover() {
  const { userId } = useContext(UserContext);
  const { t } = useI18n();
  const [prefTags, setPrefTags] = useState([]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    // Load all three preference namespaces in parallel. Each returns a list
    // of restriction strings; we union → canonicalize → dedupe.
    (async () => {
      const buckets = ['allergy', 'preference', 'dietary_style'];
      try {
        const results = await Promise.allSettled(
          buckets.map((b) => dataService.loadUserPreferences(userId, b))
        );
        if (cancelled) return;
        const unique = new Set();
        results.forEach((r) => {
          if (r.status !== 'fulfilled' || !r.value) return;
          const list = Array.isArray(r.value) ? r.value
                     : Array.isArray(r.value?.dietary_restrictions) ? r.value.dietary_restrictions
                     : Array.isArray(r.value?.restrictions) ? r.value.restrictions
                     : [];
          list.forEach((raw) => {
            const key = String(raw?.name || raw?.label || raw || '').toLowerCase().trim();
            const tag = PREF_TO_TAG[key];
            if (tag) unique.add(tag);
          });
        });
        setPrefTags(Array.from(unique));
      } catch {
        // Backend preferences optional — silently fall back to an empty
        // filter (Discover shows everything).
        if (!cancelled) setPrefTags([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <AppShell dense backdrop>
      <DiscoveryScreen
        userId={userId || null}
        withBackdrop
        initialTags={prefTags}
        title={t('discover.hero_title')}
        subtitle={t('discover.hero_subtitle')}
      />
    </AppShell>
  );
}
